import {
  CardHint,
  AddLinkedCardResponse,
  Card,
  Tag,
  TagColor,
  CreateTagErrorResponse,
  CreateTagResponse,
  LinkedCard,
  Project,
  CardHintRelation,
  HintCard,
} from '@superthread-com/api';
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import {
  SuggestionTag,
  RemoveSuggestionPayload,
  SuggestionTooltipType,
  SuggestionType,
} from '@/types/suggestions';
import constants from '@/utilities/constants';
import useTranslate from '@/utilities/composables/useTranslate';
import { nanoid } from 'nanoid';
import { ResourceType } from '@/types/resources';

const suggestedTagColors = constants.defaultSuggestedTagsColors as { [key: string]: TagColor };
const suggestionTooltipBaseText = {
  default: 'suggestedBasedOnTitle',
  accept: 'acceptSuggestion',
  reject: 'rejectSuggestion',
};

// this will be shared between composables not matter where they are imported
const nonPrivateDuplicateSuggestions = ref<Record<string, CardHint[]>>({});

export default function useSuggestions(currentCardId: string, isQuickCard: boolean = false) {
  const store = useStore();
  const { getters, dispatch } = store;
  const { translate } = useTranslate();

  function suggestionTooltip(type: SuggestionTooltipType = 'default', isTippy: boolean = false) {
    const tooltipText = translate(suggestionTooltipBaseText[type] || '');
    return isTippy
      ? { text: tooltipText }
      : {
          content: tooltipText,
          ...(type === 'default' && { placement: 'bottom' }),
        };
  }
  const getCurrentCard = computed((): Card => {
    return store.getters.getCardById(currentCardId);
  });

  const getCurrentProjectId = computed((): string => {
    return isQuickCard
      ? store.getters.getQuickCardProjectId
      : (getCurrentCard.value.project_id ?? '');
  });

  const getShowSuggestionsPlaceholder = computed((): boolean => {
    return store.getters[
      isQuickCard ? 'getQCShowSuggestionsPlaceholder' : 'getShowSuggestionsPlaceholder'
    ];
  });

  function setShowSuggestionsPlaceholder(showPlaceholder: boolean) {
    dispatch(
      isQuickCard ? 'setQCShowSuggestionsPlaceholder' : 'setShowSuggestionsPlaceholder',
      showPlaceholder
    );
  }

  function setSuggestions(suggestions: CardHint[]) {
    if (isQuickCard) {
      dispatch('setQCSuggestions', suggestions);
    } else {
      dispatch('setSuggestions', { suggestions, cardId: currentCardId });
    }
  }

  function removeSuggestion(payload: RemoveSuggestionPayload) {
    if (isQuickCard) {
      dispatch('removeQCSuggestion', payload);
    } else {
      dispatch('removeSuggestion', { payload, cardId: currentCardId });
    }
  }

  // ** TAG SUGGESTIONS **

  const getTags = computed((): { [key: string]: Tag } => {
    return getters.getTags;
  });

  const getQuickCardTags = computed((): Tag[] => {
    return getters.getQuickCard.tags ?? [];
  });

  const getCurrentCardTags = computed((): Tag[] => {
    return getCurrentCard.value.tags ?? [];
  });

  const getTagSuggestions = computed((): CardHint[] => {
    if (isQuickCard) {
      return store.getters.getQCTagSuggestions;
    } else {
      return store.getters.getTagSuggestions(currentCardId);
    }
  });

  const getExistingTag = (tagName: string = ''): Tag => {
    return (
      Object.values(getTags.value).find(
        ({ name, project_id }: Tag) =>
          name &&
          tagName.toLowerCase() === name.toLowerCase() &&
          // check if tag is in current project of a card or quick card
          // or if its a workspace tag
          (project_id === getCurrentProjectId.value || !project_id)
      ) ?? {}
    );
  };

  const isTagAlreadyAddedToCard = (existingTagId: string = ''): boolean => {
    return (isQuickCard ? getQuickCardTags : getCurrentCardTags).value.some(
      (tag) => tag.id === existingTagId
    );
  };

  // filters tag suggestions based on existing tags in project or workspace and tags already added to card
  const filteredTagSuggestions = computed((): SuggestionTag[] => {
    const suggestedTagsTemp: SuggestionTag[] = [];

    // generic feature and bug suggestions do not provide any value to epics, so we disable them
    if (getCurrentCard.value.type === ResourceType.Epic) {
      return [];
    }

    getTagSuggestions.value.forEach((suggestion: CardHint) => {
      const existingTag = getExistingTag(suggestion?.tag?.name);

      if (!Object.keys(existingTag).length) {
        suggestedTagsTemp.push({
          name: suggestion?.tag?.name ?? 'bug',
          color: suggestedTagColors[suggestion?.tag?.name ?? 'bug'],
        });
        return;
      }

      if (isTagAlreadyAddedToCard(existingTag.id)) {
        removeSuggestion({
          type: SuggestionType.tag,
          tagName: existingTag.name,
        });
        return;
      }

      suggestedTagsTemp.push({
        name: existingTag.name ?? 'bug',
        color: existingTag.color as TagColor,
      });
    });

    return suggestedTagsTemp.sort((a, b) => {
      const { name: nameA = '' } = a || {};
      const { name: nameB = '' } = b || {};
      return nameA.localeCompare(nameB);
    });
  });

  async function createTag(name: string = 'bug'): Promise<Tag> {
    const localTagId = nanoid(6);
    const color = suggestedTagColors[name ?? 'bug'];
    const newLocalTag = {
      name,
      color,
      id: localTagId,
      project_id: getCurrentProjectId.value,
      team_id: getters.getTeamID,
    };
    dispatch('addLocalTag', { tag: newLocalTag });

    return dispatch('createTag', {
      tag: {
        name,
        color,
      },
      projectId: getCurrentProjectId.value,
    })
      .then(({ data }: any) => {
        const { tag } = data as CreateTagResponse;
        dispatch('addLocalTag', { tag });
        dispatch('deleteLocalTag', { tagId: localTagId });
        return tag ?? {};
      })
      .catch((error: any) => {
        dispatch('deleteLocalTag', { tagId: localTagId });
        // tag already exists
        const { type = '', tag_id = '' } = (error?.response?.data || {}) as CreateTagErrorResponse;
        return type === 'ERR_TAG_EXISTS' && tag_id ? getTags.value[tag_id] : {};
      });
  }

  async function acceptTagSuggestion(tagName: string = '') {
    const existingTag = getExistingTag(tagName);
    const newTag: Tag = Object.keys(existingTag).length ? existingTag : await createTag(tagName);

    if (!Object.keys(newTag || {}).length) return;

    if (isQuickCard) {
      dispatch('addTagToQuickCard', { tag: newTag });
      removeSuggestion({
        type: SuggestionType.tag,
        tagName,
      });
      return;
    }

    dispatch('localAddTagToCard', {
      cardId: currentCardId,
      tag: newTag,
    });
    removeSuggestion({
      type: SuggestionType.tag,
      tagName,
    });
    dispatch('addTagToCard', {
      cardId: currentCardId,
      tagId: newTag.id,
    }).catch(() => {
      dispatch('localDeleteTagFromCard', {
        cardId: currentCardId,
        tagId: newTag.id,
      });
    });
  }

  // ** DUPLICATE SUGGESTIONS START **

  const similarityThreshold = 0.75;
  const currentCardDuplicates = computed((): LinkedCard[] => {
    return (
      getCurrentCard?.value?.linked_cards?.filter(
        (linkedCard: LinkedCard) => linkedCard.linked_card_type === 'duplicates'
      ) ?? []
    );
  });

  const getDuplicateSuggestions = computed((): CardHint[] => {
    if (isQuickCard) {
      return store.getters.getQCDuplicateSuggestions;
    } else {
      return store.getters.getDuplicateSuggestions(currentCardId);
    }
  });

  async function isSuggestedCardViable(cardId: string): Promise<Card> {
    const card = getters.getCardById(cardId);
    const willTryToFetch = !Object.keys(card).length || card.partially_loaded;
    return willTryToFetch ? dispatch('fetchCard', { cardId }) : Promise.resolve(card);
  }

  // removes suggested cards that user cannot access (cards from private spaces)
  //* filtering out non-private suggestions has to be async
  //* because we need to fetch cards in order to check if they can be accessed
  const filterOutPrivateSuggestedDuplicates = async (newSuggestions: CardHint[]) => {
    const promises = newSuggestions.map((suggestion) => {
      const { card_id = '' } = suggestion?.relation?.card ?? {};
      return isSuggestedCardViable(card_id);
    });

    const results = await Promise.allSettled(promises);
    const filteredSuggestions = results.flatMap((promise, index) => {
      return promise.status === 'fulfilled' ? [newSuggestions[index]] : [];
    });

    nonPrivateDuplicateSuggestions.value = {
      ...nonPrivateDuplicateSuggestions.value,
      [currentCardId]: filteredSuggestions,
    };
  };

  // filtered cards based on whether they are already a duplicate of current card or archived
  const filteredDuplicateSuggestions = computed((): CardHint[] => {
    const tempFiltered: CardHint[] = [];

    (nonPrivateDuplicateSuggestions.value[currentCardId] || []).forEach((suggestion) => {
      const { relation = {} as CardHintRelation } = suggestion;
      const { card: suggestedCard = {} as HintCard, similarity = 0 as number } = relation ?? {};
      const { card_id = '' } = suggestedCard;
      const card: Card = getters.getCardById(card_id);

      const updatedSuggestion = {
        ...suggestion,
        relation: {
          ...relation,
          card: {
            ...suggestedCard,
            ...card,
          },
        },
      };

      const isCardAlreadyADuplicate = currentCardDuplicates.value.some(
        (c) => c.card_id === card_id
      );

      // check if card belongs to trash project
      const belongsToTrashProject = getters.getTrashProjects.some(
        (trashProject: Project) => trashProject.id === card.project_id
      );

      // exclude archived cards
      const isCardArchived = card.archived;

      // filter out suggestion if it is added as a parent card
      const isParentCard = getCurrentCard.value.parent_card?.card_id === card_id;

      // exclude child cards
      const isChildCard = (getCurrentCard.value.child_card_order as any)?.some(
        (id: any) => id === card_id
      );

      // exclude related, blocked by, blocking cards
      const isLinkedCard = (getCurrentCard.value.linked_cards as any)?.some(
        (linkedCard: any) => linkedCard.card_id === card_id
      );

      // include suggested cards of same type only
      const isSameType = card.type === getCurrentCard.value.type;

      const fulfillsSimilarityThreshold = similarity >= similarityThreshold;

      return isSameType &&
        !isCardAlreadyADuplicate &&
        !isCardArchived &&
        !isParentCard &&
        !isChildCard &&
        !isLinkedCard &&
        !belongsToTrashProject &&
        fulfillsSimilarityThreshold
        ? tempFiltered.push(updatedSuggestion)
        : removeSuggestion({
            type: SuggestionType.relation,
            card_id,
          });
    });

    return tempFiltered;
  });

  function acceptDuplicateSuggestion(card_id: string) {
    dispatch('cardRelationships', {
      body: {
        card_id,
        linked_card_type: 'duplicates',
      },
      cardId: currentCardId,
    }).then(({ linked_card }: AddLinkedCardResponse) => {
      const { linked_cards: currentCardLinkedCards = [] } = getCurrentCard.value;
      const newLinkedCards = [...currentCardLinkedCards, linked_card];
      dispatch('localUpdateCardField', {
        cardId: currentCardId,
        fieldName: 'linked_cards',
        fieldValue: newLinkedCards,
      });
      removeSuggestion({
        type: SuggestionType.relation,
        card_id,
      });
    });
  }

  return {
    setSuggestions,
    setShowSuggestionsPlaceholder,
    getShowSuggestionsPlaceholder,
    removeSuggestion,
    suggestionTooltip,

    // ** TAG SUGGESTIONS **
    acceptTagSuggestion,
    filteredTagSuggestions,

    // ** DUPLICATE SUGGESTIONS **
    getDuplicateSuggestions,
    nonPrivateDuplicateSuggestions,
    filteredDuplicateSuggestions,
    filterOutPrivateSuggestedDuplicates,
    acceptDuplicateSuggestion,
  };
}
